import { createDisplayAnswersWithIndices } from '@/utils/arrayUtils';

/** Shuffled email UI slot (top-to-bottom), not canonical answer index (0=key). */
export type DisplaySlot = 0 | 1 | 2 | 3;

const DISPLAY_SLOT_LETTERS = ['A', 'B', 'C', 'D'] as const;

export function displaySlotToEmailParam(slot: DisplaySlot): string {
  return DISPLAY_SLOT_LETTERS[slot];
}

/** Parse wire-format display slot from `email_choice`. Accepts A–D (preferred) and legacy 0–3. */
export function displaySlotFromEmailParam(param: string | null): DisplaySlot | null | 'invalid' {
  if (param === null) return null;
  if (param === '') return 'invalid';

  const upper = param.toUpperCase();
  const letterIndex = DISPLAY_SLOT_LETTERS.indexOf(upper as (typeof DISPLAY_SLOT_LETTERS)[number]);
  if (letterIndex !== -1) return letterIndex as DisplaySlot;

  const numeric = parseInt(param, 10);
  if (!Number.isNaN(numeric) && numeric >= 0 && numeric <= 3) {
    return numeric as DisplaySlot;
  }

  return 'invalid';
}

export type EmailChoiceParamState =
  { kind: 'none' } | { kind: 'invalid' } | { kind: 'valid'; displaySlot: DisplaySlot };

/** Classify the raw `email_choice` query param. */
export function classifyEmailChoiceParam(param: string | null): EmailChoiceParamState {
  const parsed = displaySlotFromEmailParam(param);
  if (parsed === null) return { kind: 'none' };
  if (parsed === 'invalid') return { kind: 'invalid' };
  return { kind: 'valid', displaySlot: parsed };
}

export type EmailAnswerQuestionFields = {
  question_id: number;
  edition_item_id?: string | null;
  answer_order?: readonly number[] | null;
  key: string;
  distractor_1: string;
  distractor_2: string;
  distractor_3: string;
};

export type EmailPollChoiceQuestionFields = {
  item_type?: string | null;
  poll_options?: Array<{ id: string; sort_index?: number | null }> | null;
};

/**
 * Map email display slot (shuffled order shown in the newsletter HTML)
 * to the canonical answer index the game machine expects (0=key … 3=distractor_3).
 */
export function resolveEmailAnswerCanonicalIndex(
  displaySlot: DisplaySlot,
  question: EmailAnswerQuestionFields
): number {
  const shuffledAnswers = createDisplayAnswersWithIndices(question);
  const canonicalIndex = shuffledAnswers[displaySlot]?.index;
  if (canonicalIndex === undefined) {
    throw new Error(`Invalid email display slot: ${displaySlot}`);
  }
  return canonicalIndex;
}

export function resolveEmailPollOptionId(
  pollChoiceParam: string | null,
  question: EmailPollChoiceQuestionFields | null | undefined
): string | null {
  const pollOptionId = pollChoiceParam?.trim();
  if (!pollOptionId || question?.item_type !== 'poll' || !Array.isArray(question.poll_options)) {
    return null;
  }

  const options = [...question.poll_options].sort(
    (a, b) => (a.sort_index ?? 0) - (b.sort_index ?? 0)
  );
  return options.some((option) => option.id === pollOptionId) ? pollOptionId : null;
}

export function resolveEmailPollOptionIdFromDisplaySlot(
  displaySlot: DisplaySlot,
  question: EmailPollChoiceQuestionFields | null | undefined
): string | null {
  if (question?.item_type !== 'poll' || !Array.isArray(question.poll_options)) {
    return null;
  }

  const options = [...question.poll_options].sort(
    (a, b) => (a.sort_index ?? 0) - (b.sort_index ?? 0)
  );
  return options[displaySlot]?.id ?? null;
}
